import { TOGGLE_NEW_REVIEWERS, UPDATE_NEW_REVIEWERS } from "@actions/preview/send"
import { CREATE_SHARED_SCREENERS, ENQUEUE_SHARED_SCREENERS, SCREENER_BATCH_PROGRESS, SCREENER_BATCH_COMPLETE, SCREENER_BATCH_FAILED } from "@actions/shared_screeners"

import { post, DEFAULTS } from "@api/fetch"
import { addGlobalMessage } from "@api/utils/"
import { GLOBALS } from "@src/config"
import { refreshSharedScreeners } from "@api/shared_screeners"

/**
 * Toggle new reviewers
 *
 * @returns dispatch
 */

export const toggleNewReviewers = (mode, shareType, screenerRoom = null) => (dispatch, getState) => {
  const canUseForensicWatermark =
    (shareType === "screener" && getState().project.files.screener.use_forensic_watermark) ||
    (shareType === "screenerRoom" && screenerRoom.use_forensic_watermark)
  const useForensicWatermark =
    canUseForensicWatermark &&
    getState().user.auth.subscription.reviewers.remaining === 0 &&
    getState().user.auth.subscription.forensic_watermark_reviewers.remaining > 0
  dispatch({
    type: TOGGLE_NEW_REVIEWERS,
    mode,
    shareType,
    canUseForensicWatermark,
    useForensicWatermark
  })
}

/**
 * Update the new reviewers state
 *
 * @returns dispatch
 */

export const updateNewReviewers = (kind, key, value) => dispatch =>
  dispatch({ type: UPDATE_NEW_REVIEWERS, kind, key, value })

/**
 * Creates the new shared screeners (synchronous — used for link generation)
 *
 * @returns dispatch
 */
export const createSharedScreeners = payload => dispatch =>
  dispatch(post(CREATE_SHARED_SCREENERS.REQUEST, "shared-screeners", payload, null))

/**
 * Tracks the active batch ID to cancel stale polls when a new batch starts
 */
let activeBatchId = null

/**
 * Polls a screener batch until it completes or fails
 *
 * @returns dispatch
 */
const pollScreenerBatch = batchId => dispatch => {
  activeBatchId = batchId
  const poll = () => {
    if (activeBatchId !== batchId) return
    fetch(`${GLOBALS.API_URL}/shared-screeners/email-batches/${batchId}`, DEFAULTS)
      .then(res => {
        if (activeBatchId !== batchId) return
        if (!res.ok) {
          dispatch({ type: SCREENER_BATCH_FAILED })
          return
        }
        return res.json().then(({ status, created_count, total_count }) => {
          if (activeBatchId !== batchId) return
          dispatch({ type: SCREENER_BATCH_PROGRESS, payload: { createdCount: created_count, totalCount: total_count } })
          if (status === "complete") {
            dispatch({ type: SCREENER_BATCH_COMPLETE })
            dispatch(refreshSharedScreeners())
          } else if (status === "failed") {
            dispatch({ type: SCREENER_BATCH_FAILED })
          } else {
            setTimeout(poll, 2500)
          }
        })
      })
      .catch(() => {
        if (activeBatchId === batchId) dispatch({ type: SCREENER_BATCH_FAILED })
      })
  }
  poll()
}

/**
 * Generates a sharing url for a screener preview
 *
 * @returns dispatch
 */

export const generateSharingLink = () => (dispatch, getState) => {
  const newReviewers = getState().preview.send.link
  const shareType = getState().preview.send.shareType
  const payload = {
    emails: [
      {
        full_name: newReviewers.full_name,
        email: ""
      }
    ],
    sendWatchNotification: newReviewers.send_email_notification || false,
    ipProtectionEnabled: newReviewers.ip_protection_enabled || false,
    shareType: "link",
    watermark: parseWatermark(newReviewers, "link"),
    password: newReviewers.password,
    geoblockCountryCodes: newReviewers.geoblock_country_codes,
    geoblockRegionCodes: newReviewers.geoblock_region_codes,
    expiry: newReviewers.expiry,
    availableAt: newReviewers.available_at,
    viewCountLimit: newReviewers.view_count_limit,
    useForensicWatermark: newReviewers.use_forensic_watermark,
  }
  if (shareType === "screener") payload.screenerID = getState().project.files.screener._id
  else payload.screenerGroupID = getState().screener_rooms.screenerRoom.data._id
  dispatch(createSharedScreeners(payload))
}

/**
 * Send email links to screener reviewers (async via queue)
 *
 * @returns dispatch
 */

export const sendReviewerLinks = () => (dispatch, getState) => {
  activeBatchId = null
  const newReviewers = getState().preview.send.email
  const shareType = getState().preview.send.shareType
  const emails = []
  Object.keys(newReviewers.list).forEach(key => emails.push(newReviewers.list[key]))
  const payload = {
    emails,
    ccEmailAddresses: newReviewers.cc_email_addresses || "",
    message: newReviewers.message,
    sendWatchNotification: newReviewers.send_email_notification || false,
    ipProtectionEnabled: newReviewers.ip_protection_enabled || false,
    shareType: "email",
    watermark: parseWatermark(newReviewers, "email"),
    password: newReviewers.password,
    geoblockCountryCodes: newReviewers.geoblock_country_codes,
    geoblockRegionCodes: newReviewers.geoblock_region_codes,
    expiry: newReviewers.expiry,
    availableAt: newReviewers.available_at,
    viewCountLimit: newReviewers.view_count_limit,
    useForensicWatermark: newReviewers.use_forensic_watermark,
  }
  if (shareType === "screener") payload.screenerID = getState().project.files.screener._id
  else payload.screenerGroupID = getState().screener_rooms.screenerRoom.data._id
  dispatch(
    post(
      ENQUEUE_SHARED_SCREENERS.REQUEST,
      "shared-screeners/email-batches",
      payload,
      ({ batch_id }) => {
        if (batch_id) {
          dispatch(pollScreenerBatch(batch_id))
        } else {
          dispatch({ type: SCREENER_BATCH_FAILED })
        }
      },
      ({ message }) => {
        if (message) dispatch(addGlobalMessage(message, "error"))
      }
    )
  )
}

const parseWatermark = (reviewer, mode) => ({
  is_active: reviewer.watermarkEnabled,
  text: reviewer.text,
  position: reviewer.position,
  second_line_text_type: reviewer.second_line_text_type,
  size: reviewer.size,
  opacity: reviewer.opacity,
  mode
})
